import { UserBadge } from "@/components/UserBadge";
import {
  isCancelledStatus,
  isCompletedStatus,
} from "@/features/appointments/utils";
import { useCalendarContext } from "@/features/calendar/CalendarProvider";
import styled from "@emotion/styled";
import {
  Variables,
  checkmarkCircleIcon,
  circleIcon,
  pencilIcon,
} from "@repo/skapa";
import { Text } from "@repo/skapa";
import { Icon } from "@repo/skapa";
import { T } from "@repo/translation";
import { LocalizedDateAndTime, LocalizedTime, Name } from "@repo/ui";
import { useSettings } from "@repo/ui";
import { FlexDiv } from "@repo/ui";
import { hasItems } from "@repo/utils";
import { isSameDay } from "date-fns";

import { SHORT_SLOT_LENGTH } from "../constants";
import { useDragPreview } from "../team-calendar/DragPreviewContext";
import {
  type CoWorkerCalendarItem,
  isCoWorkerCalendarBokaAppointment,
  isCoWorkerCalendarOutlook,
  isCoWorkerCalendarTask,
  isCoWorkerScheduleActivity,
} from "../types";
import {
  getColor,
  getIcon,
  getPreCallTaskStatus,
  getRenderTypeInfoFromCalendarItem,
} from "../utils";
import { CalendarBlockText } from "./CalendarBlockText";
import { InternalIcon } from "./components";

const CalendarIconContainerBase = styled.div`
  & > svg {
    width: 12px;
    height: 12px;
  }
`;

const CalendarIconContainer = styled(CalendarIconContainerBase)`
  margin-right: ${Variables.space25};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleContainer = styled.div<{ layout: "row" | "column" }>`
  display: flex;
  flex-direction: ${(props) => (props.layout === "row" ? "row" : "column")};
  align-items: ${(props) => (props.layout === "row" ? "center" : "flex-start")};
  width: 100%;
  margin-top: ${(props) => (props.layout === "row" ? 0 : Variables.space50)};
`;

export const CalendarTitle = ({
  item,
  showInitials,
  layout = "row",
}: {
  item: CoWorkerCalendarItem;
  showInitials?: boolean;
  layout?: "row" | "column";
}) => {
  const { task_configuration, calendar } = useSettings();
  const renderInfo = getRenderTypeInfoFromCalendarItem(item);
  const isCompleted = isCoWorkerCalendarTask(item) && !item.active;
  const isCancelled = isCoWorkerCalendarOutlook(item)
    ? item.isCancelled
    : isCancelledStatus((item as any).status);

  const { preCallTasks, isPreCallDone } = getPreCallTaskStatus(renderInfo);
  const colorInfo = getColor(
    renderInfo,
    task_configuration?.task_types,
    calendar?.activity_configuration
  );

  return (
    <>
      <TitleContainer layout={layout}>
        <CalendarIconContainer>
          {!isCoWorkerScheduleActivity(item) && (
            <Icon
              paths={getIcon(
                renderInfo,
                undefined,
                task_configuration?.task_types
              )}
              color={colorInfo.textColor}
            />
          )}
          {hasItems(preCallTasks) && (
            <Icon
              paths={isPreCallDone ? checkmarkCircleIcon : circleIcon}
              color={colorInfo.textColor}
            />
          )}
          {isCoWorkerScheduleActivity(item) && item.editedAt && (
            <Icon paths={pencilIcon} color={colorInfo.textColor} />
          )}

          <InternalIcon item={item} />
        </CalendarIconContainer>
        <Text
          textStyle="Body.S"
          color={colorInfo.textColor}
          ellipsis={layout === "row"}
          rows={layout === "row" ? 1 : 2}
          lineThrough={isCancelled || isCompleted}
          flexGrow={1}
          bold
        >
          <CalendarBlockText item={item} />
        </Text>

        {showInitials && <UserBadge email={item.emailId} small />}
      </TitleContainer>
    </>
  );
};

const isCompleted = (item: CoWorkerCalendarItem) => {
  if (
    isCoWorkerCalendarBokaAppointment(item) &&
    isCompletedStatus(item.status)
  ) {
    return true;
  }
  if (isCoWorkerCalendarTask(item) && !item.active) {
    return true;
  }

  return false;
};

export const CalendarTime = ({ item }: { item: CoWorkerCalendarItem }) => {
  const { task_configuration, calendar } = useSettings();
  const { appointmentFilters } = useCalendarContext();
  const { dragPreview } = useDragPreview();
  const isDragging = dragPreview?.id === item.id;

  let timeText;

  const isCancelled = isCoWorkerCalendarOutlook(item)
    ? item.isCancelled
    : isCancelledStatus((item as any).status);

  const startTime =
    isDragging && dragPreview ? dragPreview.tempStartDate : item.start;
  const endTime =
    isDragging && dragPreview ? dragPreview.tempEndDate : item.end;

  if (isCompleted(item)) {
    timeText = <T translationKey="label.completed" />;
  } else if (isCancelled) {
    timeText = <T translationKey="appointment_status.CANCELLED" />;
  } else if (item.allDay) {
    timeText = <T translationKey="label.all_day" />;
  } else if (item.isDueBy) {
    timeText = <LocalizedTime date={item.end} />;
  } else {
    timeText = (
      <CalendarItemTimeRange
        fromDate={startTime}
        toDate={endTime}
        selectedDate={item.displayStart ?? appointmentFilters.selectedDate}
      />
    );
  }

  const renderInfo = getRenderTypeInfoFromCalendarItem(item);
  const colorInfo = getColor(
    renderInfo,
    task_configuration?.task_types,
    calendar?.activity_configuration
  );

  return (
    <Text
      xsmall
      ellipsis={item.length <= SHORT_SLOT_LENGTH}
      color={colorInfo.textColor}
      noWrap
    >
      {timeText}
    </Text>
  );
};

export const CalendarItemTimeRange = ({
  fromDate,
  toDate,
  selectedDate,
}: {
  fromDate: Date;
  toDate: Date;
  selectedDate: Date;
}) => {
  const isCrossDay = !isSameDay(fromDate, toDate);
  const showFromDate = !isSameDay(fromDate, selectedDate);
  const showToDate = isCrossDay || !isSameDay(toDate, selectedDate);

  return (
    <>
      {showFromDate ? (
        <LocalizedDateAndTime date={fromDate} showYear={false} />
      ) : (
        <LocalizedTime date={fromDate} />
      )}
      {" - "}
      {showToDate ? (
        <LocalizedDateAndTime date={toDate} showYear={false} />
      ) : (
        <LocalizedTime date={toDate} />
      )}
    </>
  );
};

export const MediumWeekCalendarAppointment = ({
  item,
  showInitials,
}: {
  item: CoWorkerCalendarItem;
  showInitials?: boolean;
}) => {
  return (
    <div>
      <CalendarTitle item={item} showInitials={showInitials} />
      <CalendarTime item={item} />
    </div>
  );
};

export const LongWeekCalendarAppointment = ({
  item,
  showInitials,
}: {
  item: CoWorkerCalendarItem;
  showInitials?: boolean;
}) => {
  const renderInfo = getRenderTypeInfoFromCalendarItem(item);
  const { task_configuration, calendar } = useSettings();
  const colorInfo = getColor(
    renderInfo,
    task_configuration?.task_types,
    calendar?.activity_configuration
  );

  return (
    <>
      <CalendarTitle item={item} layout="column" showInitials={showInitials} />
      <CalendarTime item={item} />
      {item.customer && (
        <FlexDiv>
          <Text xsmall color={colorInfo.textColor}>
            <Name
              firstName={item.customer.firstName}
              lastName={item.customer.lastName}
            />
          </Text>
        </FlexDiv>
      )}
    </>
  );
};
